import { DataCluster } from "./DataCluster";

export class ClusterStats {
    public readonly lstPoints: { sum: number };
    public readonly clusters: {
        count: number;
        lstPoints: {
            mean: number;
            sum: number;
            min: number;
            max: number;
        };
    };

    //
    // Create stats for display purposes (only count is used right now)
    //
    constructor(lstPoints: google.maps.Data.Feature[], clusters: DataCluster[]){
        this.lstPoints = { sum: lstPoints.length };
        const clusterPointCounts = clusters.map((a) => a.count);
        const clusterPointSum = clusterPointCounts.reduce((a, b) => a + b, 0);

        this.clusters = {
            count: clusters.length,
            lstPoints: {
                mean: clusterPointSum / clusters.length,
                sum: clusterPointSum,
                min: Math.min(...clusterPointCounts),
                max: Math.max(...clusterPointCounts)
            }
        };
    }
}

export interface Renderer {
    render(cluster: DataCluster, stats: ClusterStats, map: google.maps.Map): google.maps.Data.Feature;
}

export class DefaultRenderer implements Renderer{
    public render(
        { count, position, DisplayPoint, lstPoints }: DataCluster,
        stats: ClusterStats,
        map: google.maps.Map
    ): google.maps.Data.Feature {

        //
        // Create Group Point
        // Currently setting labels to X Students or Y Buses
        // Then it fills in the values with blanks
        // Change this if group points get their own popups (Also make sure main maps methods check Type and GroupPoint, 
        // not just type. Otherwise would have null values. This includes labels/styles)
        //
        if(count > 1){
            let GroupPoint: google.maps.Data.Point = new google.maps.Data.Point(position);
            
            
            var Type = "";
            var MainMap: google.maps.Map = null;
            
            if(DisplayPoint != null){
                Type = DisplayPoint.getProperty("Type");
                MainMap = DisplayPoint.getProperty("Map");
            }
            else if(lstPoints.length > 0){
                Type = lstPoints[0].getProperty("Type");
                MainMap = lstPoints[0].getProperty("Map");
            }
            
            var GroupProperties: any = {};

            if(Type == "Bus"){
                GroupProperties = this.CreateBusGroupPoint({ count, position, DisplayPoint, lstPoints }, MainMap);
            }
            else if(Type == "Student"){
                GroupProperties = this.CreateStudentGroupPoint({ count, position, DisplayPoint, lstPoints }, MainMap);
            }

            let GroupFeatureOptions: google.maps.Data.FeatureOptions = {
				geometry: GroupPoint,
				properties: GroupProperties,
			};

			return new google.maps.Data.Feature(GroupFeatureOptions);
        }

        //
        // Normal Entry
        // Don't generate anything, use the existing Bus or Student point
        //
        else{
            return DisplayPoint;
        }
    }

    public CreateBusGroupPoint({ count, position, DisplayPoint, lstPoints }, map: google.maps.Map): any{
        var BusProperties: any = {};

        //
        // Default Properties
        //
        BusProperties.Type = "Bus";
        BusProperties.Map = map;
        BusProperties.GroupPoint = true;
        BusProperties.Lat = position.lat();
        BusProperties.Long = position.lng();

        //
        // Label and Text Color
        //
        var BusTitle = `${count} Buses`;
        var BusTextColor = "Black";
        
        if(DisplayPoint != null){
            BusTextColor = DisplayPoint.getProperty("BusTextColor");
        }
        else if(lstPoints.length > 0){
            BusTextColor = lstPoints[0].getProperty("BusTextColor");
        }

        BusProperties.BusTextColor = BusTextColor;
        BusProperties.BusLabel = BusTitle;

        //
        // Null Values (Overwrite label properties like BusNbr with X Buses)
        //
        BusProperties.BusID = -1;
        BusProperties.BusNbr = BusTitle;
        BusProperties.BusDesc = BusTitle;
        BusProperties.Driver_PersonName = BusTitle;
        BusProperties.BusRouteDesc = BusTitle;
        BusProperties.BusRouteTypeDesc = BusTitle;
        BusProperties.Lat = position.lat();
        BusProperties.Long = position.lng();
        BusProperties.HorizontalAccuracy = 0;
        BusProperties.SpeedMph = BusTitle;
        BusProperties.ShowSpeedWarning = false;
        BusProperties.c_date = new Date();
        BusProperties.c_dateDesc = "";
        BusProperties.StudentCount = BusTitle;
        BusProperties.ToPickupCount = 0;
        BusProperties.lstStudentsOnBus = [];
        BusProperties.lstStudentsToPickUp = [];
        BusProperties.lstSchoolIDs = [];
        BusProperties.Selected = false;
        BusProperties.HasAlert = false;
        BusProperties.AlertDesc = "";
        BusProperties.IsAccident = false;
        BusProperties.Severity = 0;
        BusProperties.IsMySchool = true;

        return BusProperties;
    }

    public CreateStudentGroupPoint({ count, position, DisplayPoint, lstPoints }, map: google.maps.Map): any{
        var StudentProperties: any = {};

        //
        // Default Properties
        //
        StudentProperties.Type = "Student";
        StudentProperties.Map = map;
        StudentProperties.GroupPoint = true;
        StudentProperties.Lat = position.lat();
        StudentProperties.Long = position.lng();

        //
        // Label, Text Color, ON/OFF type
        //
        var StudentTitle = `${count} Students`;
        var EntryTypeID = 0;
        var StudentTextColor = "Black";

        if(DisplayPoint != null){
            EntryTypeID = DisplayPoint.getProperty("EntryTypeID");
            StudentTextColor = DisplayPoint.getProperty("StudentTextColor");
        }
        else if(lstPoints.length > 0){
            EntryTypeID = lstPoints[0].getProperty("EntryTypeID");
            StudentTextColor = lstPoints[0].getProperty("StudentTextColor");
        }

        StudentProperties.StudentTextColor = StudentTextColor;
        StudentProperties.StudentLabel = StudentTitle;
        StudentProperties.EntryTypeID = EntryTypeID;
        StudentProperties.EntryTypeDesc = "";

        //
        // Null Values (Overwrite label properties like BusRouteDesc with X Students)
        //
        StudentProperties.BusStudentRideTxnID = -1;
        StudentProperties.Driver_PersonID = -1;
        StudentProperties.DriverName = "";
        StudentProperties.Student_PersonID = -1;
        StudentProperties.StudentID = "";
        StudentProperties.StudentName = StudentTitle;
        StudentProperties.BusID = -1;
        StudentProperties.BusNbr = StudentTitle;
        StudentProperties.BusDesc = StudentTitle;
        StudentProperties.BusRouteDesc = StudentTitle;
        StudentProperties.Lat = position.lat();
        StudentProperties.Long = position.lng();
        StudentProperties.HorizontalAccuracy = 0;
        StudentProperties.c_date = new Date();
        StudentProperties.c_dateDesc = StudentTitle;
        StudentProperties.CurrentBusNbr = StudentTitle;
        StudentProperties.Selected = false;
        StudentProperties.LastTransaction = false;
        StudentProperties.AssignedRoute = "";
        StudentProperties.isScheduledRiderDesc = "";
        StudentProperties.IsMySchool = true;
        StudentProperties.SchoolID = -1;
        StudentProperties.SchoolName = "";
        StudentProperties.ScanTypeDesc = "";

        return StudentProperties;
    }
}